import { useEffect } from 'react'
import { api } from '@shop/api-client'
import { keys, read, write } from './store'
import type { CatalogItem, LocalShift, Payee, Station } from './store'
import type { CashSession } from '../pos/contracts'

/**
 * نسخةُ الجهاز تُحدَّث وهو متّصل — <b>كلَّ عشر دقائق وعند كلّ فتح</b> (`0052`).
 *
 * ⚠ **والفشلُ صامتٌ هنا عمدًا**: نسخةٌ لم تُحدَّث تبقى السابقة، والبيعُ المتّصلُ
 *   لا ينتظرها ولا يتعطّل بها.
 */
export async function refreshSnapshot(station: Station) {
  write(keys.station, station)

  const [catalog, payees, employees] = await Promise.allSettled([
    api<CatalogItem[]>(`/api/v1/pos/catalog?branchId=${station.branchId}&warehouseId=${station.warehouseId}`),
    api<{ employees: Payee[]; suppliers: Payee[] }>('/api/v1/cash-payouts/options'),
    api<{ userId: string; fullNameAr: string }[]>('/api/v1/attendance'),
  ])

  if (catalog.status === 'fulfilled') {
    write(keys.catalog(station.warehouseId), catalog.value)
    write(keys.catalogAt(station.warehouseId), new Date().toISOString())
  }

  if (payees.status === 'fulfilled') {
    write(keys.payees, payees.value)
  }

  if (employees.status === 'fulfilled') {
    write(keys.employees, employees.value.map((e) => ({ id: e.userId, name: e.fullNameAr })))
  }
}

export function useSnapshot(station: Station | null) {
  const branchId = station?.branchId
  const warehouseId = station?.warehouseId

  useEffect(() => {
    if (!branchId || !warehouseId) {
      return
    }

    const run = () => void refreshSnapshot({ branchId, warehouseId })

    run()

    const timer = window.setInterval(run, 10 * 60_000)

    return () => window.clearInterval(timer)
  }, [branchId, warehouseId])
}

/** الورديّةُ المفتوحةُ متّصلًا — <b>تُحفظ ليُكمل فيها إن انقطع النت</b>. */
export function rememberShift(session: CashSession | null, userId: string) {
  if (!session || session.status !== 'OPEN') {
    // لا تُمحى ورديّةٌ فُتحت فاصلًا ولم تصل بعد.
    const local = read<LocalShift | null>(keys.shift, null)

    if (local?.serverId && session === null) {
      write(keys.shift, null)
    }

    return
  }

  write(keys.shift, {
    serverId: session.id,
    clientId: null,
    sessionNo: session.sessionNo,
    userId,
    openedAt: session.openedAt,
    openingCash: session.openingCash,
  } satisfies LocalShift)
}
